import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from '../utils/api';
import { buildMediaImageDescriptor } from '../utils/mediaUrl';
import './footer.css';
import { useSettings } from '../hooks/useSettings';
import ResponsiveImage from './ResponsiveImage';

const FOOTER_GROUP_ORDER = ['Guraba\'s Choice', 'Info', 'Customer Support', 'Stay Connected'];

export default function Footer() {
  const location = useLocation();
  const [pages, setPages] = useState([]);
  const { brandName, socialLinks, address, whatsapp, brandLogo, paymentMethods } = useSettings();

  const isAdmin = location.pathname.startsWith('/admin');

  useEffect(() => {
    if (isAdmin) return; // prevent API call in admin area
    const fetchPages = async () => {
      try {
        const res = await axios.get('/api/pages/footer');
        const activePages = Array.isArray(res.data) ? res.data.filter(p => p.active) : [];
        setPages(activePages);
      } catch (err) {
        console.log('Failed to load pages', err);
      }
    };
    fetchPages();
  }, [isAdmin]);

  if (isAdmin) return <></>;

  const activePaymentMethods = paymentMethods.filter(m => m.enabled);
  const groupedPages = pages.reduce((groups, page) => {
    const groupName = page.footerGroup || 'Info';
    if (!groups[groupName]) groups[groupName] = [];
    groups[groupName].push(page);
    return groups;
  }, {});
  const pageGroups = Object.entries(groupedPages)
    .map(([groupName, groupPages]) => [
      groupName,
      [...groupPages].sort((pageA, pageB) => {
        const orderDifference = (Number(pageA.order) || 0) - (Number(pageB.order) || 0);
        if (orderDifference !== 0) return orderDifference;
        return new Date(pageA.createdAt).getTime() - new Date(pageB.createdAt).getTime();
      })
    ])
    .sort(([groupA], [groupB]) => {
      const indexA = FOOTER_GROUP_ORDER.indexOf(groupA);
      const indexB = FOOTER_GROUP_ORDER.indexOf(groupB);
      if (indexA !== -1 || indexB !== -1) {
        return (indexA === -1 ? Number.MAX_SAFE_INTEGER : indexA) - (indexB === -1 ? Number.MAX_SAFE_INTEGER : indexB);
      }
      return groupA.localeCompare(groupB);
    });

  return (
    <footer className="site-footer">
      <div className="container">
        <div className="footer-grid">
          <div className="footer-section footer-brand-section">
            {brandLogo && (
              <Link to="/" className="footer-logo-wrap" aria-label={`${brandName} home`}>
                <ResponsiveImage source={buildMediaImageDescriptor(brandLogo, '160px')} alt={`${brandName} logo`} className="footer-logo" />
              </Link>
            )}
            <h2 className="footer-title">{brandName}</h2>
          </div>

          {pageGroups.map(([groupName, groupPages]) => (
            <div className="footer-section" key={groupName}>
              <h2 className="footer-title">{groupName}</h2>
              <div className="footer-links">
                {groupPages.map(page => (
                  <Link key={page._id} to={`/page/${page.slug}`} className="footer-link">
                    {page.title}
                  </Link>
                ))}
              </div>
            </div>
          ))}

          {(address || whatsapp) && (
            <div className="footer-section">
              <h2 className="footer-title">Customer Support</h2>
              <div className="footer-contact">
                {whatsapp && (
                  <a href={`https://wa.me/${whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer">
                    WhatsApp: {whatsapp}
                  </a>
                )}
                {address && <span>{address}</span>}
              </div>
            </div>
          )}

          {(socialLinks.length > 0 || activePaymentMethods.length > 0) && (
            <div className="footer-section">
              {socialLinks.length > 0 && (
                <div className="footer-block">
                  <h2 className="footer-title">Stay Connected</h2>
                  <div className="social-links">
                    {socialLinks.map((link, index) => (
                      <a key={index} href={link.url} target="_blank" rel="noopener noreferrer" className="social-link" aria-label={link.name || 'Social link'}>
                        <i className={link.icon}></i>
                      </a>
                    ))}
                  </div>
                </div>
              )}
              {activePaymentMethods.length > 0 && (
                <div className="footer-block">
                  <h2 className="footer-title">Payment Methods</h2>
                  <div className="footer-payment-methods">
                    {activePaymentMethods.map(m => (
                      <div key={m.name} className="footer-payment-method">
                        {m.logo ? (
                          <ResponsiveImage source={buildMediaImageDescriptor(m.logo, '100px')} alt={m.name} />
                        ) : (
                          <span>
                            {m.name.toUpperCase()}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <p className="footer-copy">Copyright © {new Date().getFullYear()} {brandName}</p>
      </div>
    </footer>
  );
}
